import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Mail } from 'lucide-react';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/forms/TextField';
import { OtpInput } from '@/components/ui/OtpInput';
import { useProfile } from '@/hooks/useProfile';
import { useLogout } from '@/hooks/useLogout';
import {
  useStartEmailReplacement,
  useVerifyCurrentEmailOtp,
  useVerifyNewEmailOtp,
} from '@/hooks/useEmailReplacement';
import { mapApiError } from '@/lib/utils/error.utils';
import { API_ERROR_CODES } from '@/lib/constants/error-codes';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { OTP_UI } from '@/lib/constants/otp';
import { toast } from '@/lib/toast';
import { replaceEmailSchema, type ReplaceEmailFormValues } from './replace-email.schema';
import type { EmailReplacementStatus } from '@/lib/types/email-replacement.types';

const P = UI_STRINGS.PROFILE;
const ICON_SIZE = 16;
const FORM_NEW_EMAIL = 'replace-email-new-email-form';
const FORM_CURRENT_OTP = 'replace-email-current-otp-form';
const FORM_NEW_OTP = 'replace-email-new-otp-form';
const CODE_PATTERN = new RegExp(`^\\d{${OTP_UI.CODE_LENGTH}}$`);

/**
 * Guided steps for the double-OTP email replacement flow (FR-14/FR-68,
 * MYN-76). Private to this component - callers only see `open`/`onOpenChange`.
 */
type Step = 'newEmail' | 'currentOtp' | 'newOtp' | 'success';

const TITLE_BY_STEP: Record<Step, string> = {
  newEmail: P.REPLACE_EMAIL_NEW_EMAIL_STEP_TITLE,
  currentOtp: P.REPLACE_EMAIL_CURRENT_OTP_STEP_TITLE,
  newOtp: P.REPLACE_EMAIL_NEW_OTP_STEP_TITLE,
  success: P.REPLACE_EMAIL_SUCCESS_TITLE,
};

// Step 1 (HANDLE rows 1 and 2): the free-mail blocklist rejection and the
// collision case (which reuses the existing EMAIL_ALREADY_REGISTERED code,
// not a new one) are both surfaced before any OTP is sent.
const START_ERROR_BY_CODE: Record<string, string> = {
  [API_ERROR_CODES.EMAIL_REPLACEMENT_NOT_COMPANY_EMAIL]: P.REPLACE_EMAIL_NOT_COMPANY_EMAIL_ERROR,
  [API_ERROR_CODES.EMAIL_ALREADY_REGISTERED]: P.REPLACE_EMAIL_DUPLICATE_ERROR,
  // The one self-service replacement is already spent (UX sheet r74). The
  // dialog is still open, so this reports inline like the rows above.
  [API_ERROR_CODES.EMAIL_REPLACEMENT_ALREADY_USED]: P.REPLACE_EMAIL_ALREADY_USED_ERROR,
};

// Shared by both OTP steps (HANDLE rows 3 and 4: lockout and expiry). There
// is no resend endpoint for this flow, so both map to the same "start over"
// affordance rather than a resend action.
const OTP_ERROR_BY_CODE: Record<string, string> = {
  [API_ERROR_CODES.OTP_INVALID]: P.REPLACE_EMAIL_OTP_INVALID_ERROR,
  [API_ERROR_CODES.OTP_NOT_FOUND]: P.REPLACE_EMAIL_OTP_INVALID_ERROR,
  [API_ERROR_CODES.OTP_LOCKED]: P.REPLACE_EMAIL_OTP_LOCKED_ERROR,
  [API_ERROR_CODES.EMAIL_REPLACEMENT_REQUEST_EXPIRED]: P.REPLACE_EMAIL_REQUEST_EXPIRED_ERROR,
  [API_ERROR_CODES.EMAIL_REPLACEMENT_REQUEST_NOT_FOUND]: P.REPLACE_EMAIL_REQUEST_EXPIRED_ERROR,
};

export interface ReplaceEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * "Replace email" modal opened from `IndividualAccount` (AC2). Composes the
 * shared `Dialog` primitive - steps: new-email entry -> current-email OTP ->
 * new-email OTP -> success/handoff. The invite-accept handoff itself (when
 * `pendingInvite` is present) is out of scope here; this only surfaces that
 * it will happen.
 */
export function ReplaceEmailDialog({ open, onOpenChange }: ReplaceEmailDialogProps) {
  const { data: profile } = useProfile();
  const logout = useLogout();
  const [step, setStep] = useState<Step>('newEmail');
  const [requestId, setRequestId] = useState<string>();
  const [newEmail, setNewEmail] = useState('');
  const [currentCode, setCurrentCode] = useState('');
  const [currentCodeError, setCurrentCodeError] = useState<string>();
  const [newCode, setNewCode] = useState('');
  const [newCodeError, setNewCodeError] = useState<string>();
  const [completedStatus, setCompletedStatus] = useState<EmailReplacementStatus>();

  const startReplacement = useStartEmailReplacement();
  const verifyCurrent = useVerifyCurrentEmailOtp();
  const verifyNew = useVerifyNewEmailOtp();

  const {
    register,
    handleSubmit,
    reset: resetForm,
    formState: { errors },
  } = useForm<ReplaceEmailFormValues>({
    resolver: zodResolver(replaceEmailSchema),
    defaultValues: { newEmail: '' },
  });

  /** Back to step 1 with a clean slate (HANDLE rows 3/4: lockout/expiry both "start over"). */
  const resetFlow = () => {
    setStep('newEmail');
    setRequestId(undefined);
    setNewEmail('');
    setCurrentCode('');
    setCurrentCodeError(undefined);
    setNewCode('');
    setNewCodeError(undefined);
    setCompletedStatus(undefined);
    resetForm();
    startReplacement.reset();
    verifyCurrent.reset();
    verifyNew.reset();
  };

  useEffect(() => {
    if (!open) resetFlow();
    // Mutations are deliberately omitted - useMutation returns a new object
    // every render, so including them would re-run this effect (and call
    // .reset()) on every render instead of only on open/close. Same pattern
    // as ChangePasswordDialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // `stopPropagation` on all three submits below is load-bearing, not
  // defensive styling: React portals a Dialog's content to `document.body`
  // in the real DOM, but for event bubbling it stays a descendant of
  // wherever it's declared in the *React tree* - originally the old `/me/edit`
  // page's own `<form onSubmit={onSave}>` (removed by MYN-217; kept so the
  // dialog stays safe wherever it is declared inside a form). A submit
  // event's `preventDefault()` only cancels that target form's native
  // submission; it does not stop the same synthetic event from continuing
  // to bubble (via the React tree, not the DOM tree) into the outer form's
  // `onSubmit`, which re-saves the profile and navigates to `/me` -
  // silently killing this dialog mid-flow. Reproduced and confirmed via a
  // live trace before this fix; see the ticket spec for the repro.
  const onSubmitNewEmail = handleSubmit((values) => {
    startReplacement.mutate(values.newEmail, {
      onSuccess: (data) => {
        setRequestId(data.requestId);
        setNewEmail(values.newEmail);
        setStep('currentOtp');
      },
    });
  });

  const handleVerifyCurrent = (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!CODE_PATTERN.test(currentCode)) {
      setCurrentCodeError(P.REPLACE_EMAIL_CODE_REQUIRED);
      return;
    }
    setCurrentCodeError(undefined);
    if (!requestId) return;
    verifyCurrent.mutate({ requestId, code: currentCode }, { onSuccess: () => setStep('newOtp') });
  };

  const handleVerifyNew = (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!CODE_PATTERN.test(newCode)) {
      setNewCodeError(P.REPLACE_EMAIL_CODE_REQUIRED);
      return;
    }
    setNewCodeError(undefined);
    if (!requestId) return;
    verifyNew.mutate(
      { requestId, code: newCode },
      {
        onSuccess: (data) => {
          setCompletedStatus(data);
          setStep('success');
        },
      },
    );
  };

  /**
   * The Backend invalidates the current session as part of completing the
   * swap (its JWT still claims the old email otherwise, e.g. `Create
   * Startup`'s eligibility check would keep rejecting the account as if
   * nothing changed) - so success here is not "close the dialog", it's
   * "log out and route to login", same explicit pattern
   * `DangerZoneCard`'s delete-account flow already uses. Success
   * still closes the surface and reports via toast (ui-guidelines.md §12);
   * `logout()` provides both the navigation and the toast fires first so it
   * is not lost to the page unmount.
   */
  const handleDone = () => {
    toast.success(P.REPLACE_EMAIL_SUCCESS_TOAST);
    onOpenChange(false);
    logout();
  };

  const newEmailError = startReplacement.isError
    ? mapApiError(startReplacement.error, START_ERROR_BY_CODE, P.REPLACE_EMAIL_START_ERROR)
    : undefined;
  const currentOtpError = verifyCurrent.isError
    ? mapApiError(verifyCurrent.error, OTP_ERROR_BY_CODE, P.REPLACE_EMAIL_VERIFY_CURRENT_ERROR)
    : undefined;
  const newOtpError = verifyNew.isError
    ? mapApiError(verifyNew.error, OTP_ERROR_BY_CODE, P.REPLACE_EMAIL_VERIFY_NEW_ERROR)
    : undefined;

  let footer: ReactNode;
  if (step === 'newEmail') {
    footer = (
      <>
        <Button type="submit" form={FORM_NEW_EMAIL} variant="green" disabled={startReplacement.isPending}>
          {startReplacement.isPending ? P.REPLACE_EMAIL_SENDING : P.REPLACE_EMAIL_SEND_CODE}
        </Button>
        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={startReplacement.isPending}>
          {P.CANCEL}
        </Button>
      </>
    );
  } else if (step === 'currentOtp') {
    footer = (
      <>
        <Button type="submit" form={FORM_CURRENT_OTP} variant="green" disabled={verifyCurrent.isPending}>
          {verifyCurrent.isPending ? P.REPLACE_EMAIL_VERIFYING : P.REPLACE_EMAIL_VERIFY}
        </Button>
        <Button variant="outline" onClick={resetFlow} disabled={verifyCurrent.isPending}>
          {P.REPLACE_EMAIL_START_OVER}
        </Button>
      </>
    );
  } else if (step === 'newOtp') {
    footer = (
      <>
        <Button type="submit" form={FORM_NEW_OTP} variant="green" disabled={verifyNew.isPending}>
          {verifyNew.isPending ? P.REPLACE_EMAIL_VERIFYING : P.REPLACE_EMAIL_VERIFY}
        </Button>
        <Button variant="outline" onClick={resetFlow} disabled={verifyNew.isPending}>
          {P.REPLACE_EMAIL_START_OVER}
        </Button>
      </>
    );
  } else {
    footer = (
      <Button variant="green" onClick={handleDone}>
        {P.REPLACE_EMAIL_DONE}
      </Button>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={TITLE_BY_STEP[step]}
      showCloseButton={step !== 'success'}
      footer={footer}
    >
      {step === 'newEmail' && (
        <form
          id={FORM_NEW_EMAIL}
          onSubmit={(event) => {
            event.stopPropagation();
            void onSubmitNewEmail(event);
          }}
          noValidate
          className="flex flex-col gap-4"
        >
          {newEmailError && (
            <p role="alert" className="text-sm text-danger">
              {newEmailError}
            </p>
          )}
          <TextField
            {...register('newEmail')}
            label={P.REPLACE_EMAIL_NEW_EMAIL_LABEL}
            labelVariant="caps"
            type="email"
            autoComplete="email"
            icon={<Mail size={ICON_SIZE} />}
            helpText={P.REPLACE_EMAIL_NEW_EMAIL_HINT}
            error={errors.newEmail?.message}
          />
        </form>
      )}

      {step === 'currentOtp' && (
        <form
          id={FORM_CURRENT_OTP}
          onSubmit={handleVerifyCurrent}
          noValidate
          className="flex flex-col items-center gap-4"
        >
          {currentOtpError && (
            <p role="alert" className="text-center text-sm text-danger">
              {currentOtpError}
            </p>
          )}
          <p className="text-center text-sm text-slate">
            {P.REPLACE_EMAIL_CODE_SENT_PREFIX}
            <span className="font-semibold text-navy">{profile?.email}</span>
          </p>
          <OtpInput
            label={P.REPLACE_EMAIL_CODE_LABEL}
            value={currentCode}
            onChange={setCurrentCode}
            length={OTP_UI.CODE_LENGTH}
            error={currentCodeError}
            disabled={verifyCurrent.isPending}
          />
        </form>
      )}

      {step === 'newOtp' && (
        <form
          id={FORM_NEW_OTP}
          onSubmit={handleVerifyNew}
          noValidate
          className="flex flex-col items-center gap-4"
        >
          {newOtpError && (
            <p role="alert" className="text-center text-sm text-danger">
              {newOtpError}
            </p>
          )}
          <p className="text-center text-sm text-slate">
            {P.REPLACE_EMAIL_CODE_SENT_PREFIX}
            <span className="font-semibold text-navy">{newEmail}</span>
          </p>
          <OtpInput
            label={P.REPLACE_EMAIL_CODE_LABEL}
            value={newCode}
            onChange={setNewCode}
            length={OTP_UI.CODE_LENGTH}
            error={newCodeError}
            disabled={verifyNew.isPending}
          />
        </form>
      )}

      {step === 'success' && (
        <div className="flex flex-col items-center gap-3 text-center">
          <p className="text-sm text-slate">{P.REPLACE_EMAIL_SUCCESS_BODY}</p>
          <p className="text-sm text-slate">{P.REPLACE_EMAIL_SUCCESS_AUTHENTICATOR}</p>
          {completedStatus?.pendingInvite && (
            <p className="text-sm text-navy">
              {P.REPLACE_EMAIL_SUCCESS_HANDOFF_PREFIX}
              <span className="font-semibold">{completedStatus.pendingInvite.entityName}</span>
              {P.REPLACE_EMAIL_SUCCESS_HANDOFF_SUFFIX}
            </p>
          )}
        </div>
      )}
    </Dialog>
  );
}
